import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArticleType } from '../../../types/articleType';
import { CenterProductArticleType } from '../../../types/centerProductArticleType';
import { DocumentTypeLabel } from '../../../types/testType';
import { useAuth0Token } from '../hooks/useAuth0Token';
import { useDocumentData } from '../hooks/Documents/useDocumentData';
import Article from '../pages/Article/Article';
import { LoaderPage } from '../pages/LoaderPage';
import { NafProductArticle } from '../pages/nafCenter/product/NafProductArticle';
import { TopicPageApp } from '../pages/TopicPageApp/TopicPageApp';
import { TopicPageAppPropertiesType } from '../../../types/CategoryAppType';
import { nafCenterBasePath } from './NafCenterRouter';
import { NotFound } from '../loadable-elements/NotFound';

const legacyCenterPrefix = `/${nafCenterBasePath}/naf-senter/`;
const strippedCenterPrefix = `/${nafCenterBasePath}/`;

export const NafCenterTypeSwitch = ({ slug }: { slug: string }) => {
  const { simpleToken } = useAuth0Token();
  const { meta, data } = useDocumentData(slug, simpleToken);
  const location = useLocation();
  const navigate = useNavigate();

  // Only the reserved /naf-senter/ segment right after the base path is legacy - slugs ending in
  // "naf-senter" (e.g. mobilt-naf-senter) must be left untouched.
  const isLegacyCenterPath = location.pathname.startsWith(legacyCenterPrefix);
  const locationStripped = location.pathname.replace(/\/$/, '').replace(legacyCenterPrefix, strippedCenterPrefix);
  // Normalize path segments once so slug checks are case-insensitive and robust for nested routes.
  const locationSegments = locationStripped
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.toLowerCase());

  useEffect(() => {
    if (
      isLegacyCenterPath &&
      data &&
      navigate &&
      ((data as CenterProductArticleType).type === DocumentTypeLabel.nafCenterProductArticle ||
        (data as ArticleType).type === DocumentTypeLabel.nafnoArticle)
    ) {
      const redirectUrl = location.pathname.replace(legacyCenterPrefix, strippedCenterPrefix);
      navigate(redirectUrl);
    }
  }, [location, data, navigate, isLegacyCenterPath]);

  if (data) {
    const articleData = data as ArticleType;
    const articleSlug = articleData.slug?.toLowerCase();
    const articleParentSlug = articleData.parent?.slug?.toLowerCase();
    const isNafCenterProductParent = articleData.parent?.type === DocumentTypeLabel.nafCenterProductArticle;
    const hasParentInUrl = !!articleParentSlug && locationStripped.includes(articleParentSlug);
    // For nafnoArticle under a NAF Center product, enforce /{parentSlug}/{articleSlug}.
    const hasValidNafCenterProductParentPath =
      !!articleParentSlug &&
      locationSegments[locationSegments.length - 2] === articleParentSlug &&
      locationSegments[locationSegments.length - 1] === articleSlug;

    // Check if article belongs in NAF Center tree
    const isNafCenterTopicPage = data.type === DocumentTypeLabel.topicPage && data.category?.slug === nafCenterBasePath;

    const isRootNafCenterCategoryArticle =
      articleData.type === DocumentTypeLabel.nafnoArticle &&
      articleData.category &&
      articleData.category?.slug === nafCenterBasePath;

    const isNafCentersArticle =
      articleData.type === DocumentTypeLabel.nafnoArticle &&
      articleData.category &&
      articleData.category?.slug === 'naf-senter' &&
      !isNafCenterProductParent;

    // Check if article parent equals current product name
    const isNafCenterProductSubArticle =
      articleData.type === DocumentTypeLabel.nafnoArticle &&
      articleData.parent &&
      (isNafCenterProductParent ? hasValidNafCenterProductParentPath : hasParentInUrl);

    // Check if article is a NAF Center Product article
    const isNafCentersProductArticle =
      (data as CenterProductArticleType).type === DocumentTypeLabel.nafCenterProductArticle &&
      // If a parent is defined, the parent slug must be present in the URL.
      (!(data as CenterProductArticleType).parent?.slug ||
        locationSegments.includes((data as CenterProductArticleType).parent.slug.toLowerCase()));

    if (isRootNafCenterCategoryArticle || isNafCentersArticle || isNafCenterProductSubArticle) {
      // Common NAF Center article
      return <Article article={data as ArticleType} />;
    }
    if (isNafCentersProductArticle) {
      // NAF Center Product article
      return <NafProductArticle centerProductArticle={data as CenterProductArticleType} productSlug={slug} />;
    }
    if (isNafCenterTopicPage) {
      const topicPage = data as unknown as TopicPageAppPropertiesType;
      return <TopicPageApp data={topicPage.category} asArticleData={topicPage} />;
    }
    // Regardless of data - If the document doesnt belong in the NAF Center tree, render 404 page
    return <NotFound />;
  }
  if (!data && meta.isUpdating === true) {
    // If there is no data, but the document is loading -> render loader
    return <LoaderPage />;
  }
  if (!data && !meta.isUpdating) {
    return <NotFound />;
  }
  return null;
};
